import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { Lead, LeadPerson, Person } from '@/types/leads';

/** Sentinel place ID for the "Just added" synthetic group (deprecated — kept
 *  so existing imports still compile). Manual contacts now get one virtual
 *  Lead per person, with id = MANUAL_LEAD_PREFIX + personId. */
export const JUST_ADDED_PLACE_ID = '__just_added__';
export const MANUAL_LEAD_PREFIX = 'manual-';

export const isManualLeadId = (id: string) => id.startsWith(MANUAL_LEAD_PREFIX);
export const manualLeadIdForPerson = (personId: number) => `${MANUAL_LEAD_PREFIX}${personId}`;

export type EmailFilter = 'all' | 'new' | '1' | '2+' | 'failed';
export type WhatsappFilter = 'all' | 'new' | '1' | '2+' | 'failed';

const KEYS = {
  all: ['recipients'] as const,
  list: (emailFilter: EmailFilter, search: string, whatsappFilter?: WhatsappFilter) =>
    [...KEYS.all, 'list', emailFilter, search, whatsappFilter || 'all'] as const,
};

interface PersonsPage {
  count: number;
  distinct_places?: number;
  lead_count?: number;
  next: string | null;
  previous: string | null;
  results: Person[];
}

const PAGE_SIZE = 20;

export type ContactMode = 'email' | 'phone';

/** `hide` drops anyone with any failed log on that channel. `only` keeps only
 *  those with at least one failed log (may have succeeded too — wider than the
 *  channel's `filter='failed'` bucket). Omitted = no filter on failures. */
export type FailedMode = 'all' | 'hide' | 'only';

interface UseRecipientsOptions {
  emailFilter?: EmailFilter;
  whatsappFilter?: WhatsappFilter;
  interestFilter?: 'all' | 'interested' | 'not_interested';
  search?: string;
  mode?: ContactMode;
  /** ISO timestamp; backend filters Person.createdAt >= this */
  createdAfter?: string;
  /** ISO timestamp; backend filters Person.createdAt < this */
  createdBefore?: string;
  emailFailedMode?: FailedMode;
  whatsappFailedMode?: FailedMode;
}

export const useRecipients = (options?: UseRecipientsOptions) => {
  const emailFilter = options?.emailFilter || 'all';
  const whatsappFilter = options?.whatsappFilter || 'all';
  const interestFilter = options?.interestFilter || 'all';
  const search = options?.search || '';
  const mode = options?.mode || 'email';
  const createdAfter = options?.createdAfter || '';
  const createdBefore = options?.createdBefore || '';
  const emailFailedMode = options?.emailFailedMode ?? 'all';
  const whatsappFailedMode = options?.whatsappFailedMode ?? 'all';

  return useInfiniteQuery<PersonsPage>({
    queryKey: [
      ...KEYS.list(emailFilter, search, whatsappFilter),
      mode,
      interestFilter,
      createdAfter,
      createdBefore,
      emailFailedMode,
      whatsappFailedMode,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: pageParam as number,
      };
      if (mode === 'phone') {
        params.has_phone = '1';
      } else {
        params.has_email = '1';
      }
      if (emailFilter !== 'all') {
        params.email_filter = emailFilter;
      }
      if (whatsappFilter !== 'all') {
        params.whatsapp_filter = whatsappFilter;
      }
      if (interestFilter === 'interested') {
        params.interested = 'true';
      } else if (interestFilter === 'not_interested') {
        params.interested = 'false';
      }
      if (search) {
        params.search = search;
      }
      if (createdAfter) {
        params.created_after = createdAfter;
      }
      if (createdBefore) {
        params.created_before = createdBefore;
      }
      if (emailFailedMode === 'hide') {
        params.exclude_email_failed = '1';
      } else if (emailFailedMode === 'only') {
        params.only_email_failed = '1';
      }
      if (whatsappFailedMode === 'hide') {
        params.exclude_whatsapp_failed = '1';
      } else if (whatsappFailedMode === 'only') {
        params.only_whatsapp_failed = '1';
      }

      const { data } = await api.get(API_ROUTES.persons, { params });
      if (data.results) {
        return data as PersonsPage;
      }
      return {
        count: data.length,
        next: null,
        previous: null,
        results: data,
      };
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.next) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded;
    },
  });
};

interface FetchAllFilteredRecipientsOptions {
  emailFilter?: EmailFilter;
  whatsappFilter?: WhatsappFilter;
  interestFilter?: 'all' | 'interested' | 'not_interested';
  search?: string;
  mode?: ContactMode;
  createdAfter?: string;
  createdBefore?: string;
  emailFailedMode?: FailedMode;
  whatsappFailedMode?: FailedMode;
}

/** Fetch ALL persons matching a filter (no pagination). For bulk select. */
export const fetchAllFilteredRecipients = async (
  options: FetchAllFilteredRecipientsOptions = {},
): Promise<Person[]> => {
  const {
    emailFilter = 'all',
    whatsappFilter,
    interestFilter,
    search,
    mode = 'email',
    createdAfter,
    createdBefore,
    emailFailedMode,
    whatsappFailedMode,
  } = options;

  const params: Record<string, string> = {};
  if (mode === 'phone') {
    params.has_phone = '1';
  } else {
    params.has_email = '1';
  }
  if (emailFilter !== 'all') {
    params.email_filter = emailFilter;
  }
  if (whatsappFilter && whatsappFilter !== 'all') {
    params.whatsapp_filter = whatsappFilter;
  }
  if (interestFilter === 'interested') {
    params.interested = 'true';
  } else if (interestFilter === 'not_interested') {
    params.interested = 'false';
  }
  if (search) {
    params.search = search;
  }
  if (createdAfter) {
    params.created_after = createdAfter;
  }
  if (createdBefore) {
    params.created_before = createdBefore;
  }
  if (emailFailedMode === 'hide') {
    params.exclude_email_failed = '1';
  } else if (emailFailedMode === 'only') {
    params.only_email_failed = '1';
  }
  if (whatsappFailedMode === 'hide') {
    params.exclude_whatsapp_failed = '1';
  } else if (whatsappFailedMode === 'only') {
    params.only_whatsapp_failed = '1';
  }
  const { data } = await api.get(API_ROUTES.personsSelectAll, { params });
  return data;
};

/** Flatten all pages and group persons by their parent place into Lead[].
 *  Manual (place-less) persons become one virtual Lead each, rather than a
 *  single bucketed "Just added" group. This keeps the list honest to the
 *  user's mental model: if I added 34 contacts, I should see 34 rows.
 */
export const groupRecipientsAsLeads = (data: { pages: PersonsPage[] } | undefined): Lead[] => {
  if (!data) return [];

  const placeMap = new Map<string, Lead>();
  const manualLeads: Lead[] = [];

  for (const page of data.pages) {
    for (const person of page.results) {
      const leadPerson: LeadPerson = {
        id: person.id,
        fullName: person.fullName,
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        phoneNumber: person.phoneNumber,
        createdAt: person.createdAt,
        emailLogs_count: person.emailLogs_count,
        emailLogs_successful_count: person.emailLogs_successful_count,
        emailLogs_failed_count: person.emailLogs_failed_count,
        whatsappLogs_count: person.whatsappLogs_count,
        whatsappLogs_successful_count: person.whatsappLogs_successful_count,
        whatsappLogs_failed_count: person.whatsappLogs_failed_count,
        isInterested: person.isInterested,
      };

      if (!person.place) {
        // Manual contact: one virtual Lead per person, keyed by person id.
        // displayName gets wrapped in the {text: ...} envelope so parseDisplayName()
        // handles it the same way it handles Google Places data.
        const label =
          (person.fullName || '').trim() || person.email || person.phoneNumber || 'Contact';
        manualLeads.push({
          id: manualLeadIdForPerson(person.id),
          displayName: JSON.stringify({ text: label }),
          formattedAddress: null,
          internationalPhoneNumber: person.phoneNumber,
          createdAt: person.createdAt,
          persons: [leadPerson],
        });
        continue;
      }

      const placeId = person.place.id;

      if (!placeMap.has(placeId)) {
        placeMap.set(placeId, {
          id: placeId,
          displayName: person.place.displayName,
          formattedAddress: person.place.formattedAddress,
          internationalPhoneNumber: person.place.internationalPhoneNumber,
          createdAt: person.place.createdAt,
          persons: [],
        });
      }

      placeMap.get(placeId)!.persons.push(leadPerson);
    }
  }

  const leads = Array.from(placeMap.values());

  // Manual contacts at the top so freshly-added rows are immediately visible.
  return [...manualLeads, ...leads];
};
